import { useQuery, useMutation } from '@tanstack/react-query';
import { CustomerAPI, NewoChatApi } from '@newo/api/gateway';
import type {
    CreatePartnerOrganizationPayload,
    TAttributesParams,
    TAttributeData,
    TAccountLinkData,
    TAccountLinkParams,
    TCustomerSessionReportParams,
    ActorData
} from '@newo/api/gateway';
import axios, { chatAPIInstance } from '@/utils/api/axiosInstance';

const customerAPI = new CustomerAPI(axios);
const chatApi = new NewoChatApi(chatAPIInstance);
// UPDATE Customer Attributes
export const useCustomerUpdateAttributesMutation = () =>
    useMutation({
        mutationFn: async ({ data }: { data: TAttributeData[] }) =>
            customerAPI.updateCustomerAttributes(data)
    });

// GET Customer Attributes
export const useCustomerAttributesQuery = (params?: TAttributesParams) =>
    useQuery({
        queryKey: ['customer/attributes', params],
        queryFn: async () => customerAPI.getCustomerAttributes(params),
        select: (data) => {
            return data.reduce<Record<string, string>>(
                (accumulator, attribute) => {
                    accumulator[attribute.idn] = attribute.value;
                    return accumulator;
                },
                {}
            );
        }
    });

// Get customer account links
export const useCustomerAccountLinksQuery = (params?: TAccountLinkParams) =>
    useQuery({
        queryKey: ['customer/account-links', params],
        queryFn: async () => customerAPI.getCustomerAccountLinks(params)
    });

// Create customer account link
export const useCreateAccountLinkMutation = () =>
    useMutation({
        mutationFn: async ({ data }: { data: TAccountLinkData }) =>
            customerAPI.createCustomerAccountLink(data)
    });

// Update customer account link
export const useUpdateAccountLinkMutation = () =>
    useMutation({
        mutationFn: async ({
            accountId,
            data
        }: {
            accountId: string;
            data: TAccountLinkData;
        }) => customerAPI.updateCustomerAccountLink(accountId, data)
    });

// Delete customer account link
export const useDeleteAccountLinkMutation = () =>
    useMutation({
        mutationFn: async ({ accountId }: { accountId: string }) =>
            customerAPI.deleteCustomerAccountLink(accountId)
    });

// Send customer session list report to email
export const useSendSessionReportToEmailMutation = () =>
    useMutation({
        mutationFn: async ({ data }: { data: TCustomerSessionReportParams }) =>
            customerAPI.exportCustomerSessionReportToEmail(data)
    });

export const useMyAccountLinkQuery = (isEnabled: boolean) =>
    useQuery({
        enabled: isEnabled,
        queryKey: ['customer/account-links/me'],
        queryFn: customerAPI.getMyAccountLink
    });

export const useCreatePartnerOrganization = () => {
    return useMutation({
        mutationFn: async (payload: CreatePartnerOrganizationPayload) => {
            return chatApi.createPartnerOrganization(payload);
        }
    });
};

export const useCreateActor = () => {
    return useMutation({
        mutationFn: async (actor: ActorData) => chatApi.createActor(actor)
    });
};
